import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ConfirmationService, MessageService, SharedModule } from 'primeng/api';
import { Button } from 'primeng/button';
import { IconField } from 'primeng/iconfield';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputIcon } from 'primeng/inputicon';
import { InputText } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { Tooltip } from 'primeng/tooltip';
import { debounceTime, Subject } from 'rxjs';
import CarClass from '../../../../types/car-class/CarClass';
import CarClassWriteDTO from '../../../../types/car-class/CarClassWriteDTO';
import Page from '../../../../types/shared/Page';
import { IsAdminDirective } from '../../directives/is-admin.directive';
import { CarClassService } from '../../services/car-class.service';
import { ExportService } from '../../services/export.service';
import {
    CreateCarClassDialogComponent
} from '../shared/car-classes/create-car-class-dialog/create-car-class-dialog.component';
import {
    UpdateCarClassDialogComponent
} from '../shared/car-classes/update-car-class-dialog/update-car-class-dialog.component';
import { PageHeaderComponent } from '../shared/page-header/page-header.component';

@Component({
    selector: 'app-car-classes',
    templateUrl: './car-classes.component.html',
    styleUrls: [ './car-classes.component.scss' ],
    imports: [
        PageHeaderComponent,
        IconField,
        InputIcon,
        FormsModule,
        TableModule,
        Button,
        SharedModule,
        CreateCarClassDialogComponent,
        UpdateCarClassDialogComponent,
        InputText,
        RouterLink,
        IftaLabelModule,
        IsAdminDirective,
        Tooltip
    ]
})
export class CarClassesComponent implements OnInit {
    public search = '';
    public carClasses: CarClass[] = [];
    public totalRecords = 0;
    public rows = 10;
    public currentPage = 0;
    public loading = false;
    public displayCreateCarClassDialog = false;
    public displayUpdateCarClassDialog = false;
    public selectedCarClass: CarClassWriteDTO = new CarClassWriteDTO({});

    private searchSubject = new Subject<string>();

    constructor(private carClassService: CarClassService,
                private messageService: MessageService,
                private confirmationService: ConfirmationService,
                private exportService: ExportService) {
    }

    /**
     * Initializes the component.
     */
    public ngOnInit(): void {
        this.loadCarClasses();

        // Debounce the search input to prevent too many requests
        this.searchSubject.pipe(debounceTime(300)).subscribe(() => {
            this.currentPage = 0; // Reset to the first page on new search
            this.loadCarClasses();
        });
    }

    /**
     * Loads the car classes.
     */
    public loadCarClasses(): void {
        this.loading = true;

        const size = this.rows;
        const search = this.search || '';

        this.carClassService
            .getCarClasses(search, this.currentPage, size)
            .then((result: Page<CarClass>) => {
                this.carClasses = result.content;
                this.totalRecords = result.page.totalElements;
            })
            .catch((error: any) => {
                this.messageService.add({ severity: 'error', summary: 'Error', detail: error.message, life: 3000 });
            })
            .finally(() => {
                this.loading = false;
            });
    }

    /**
     * Handles the page change event.
     *
     * @param event The page change event.
     */
    public onPageChange(event: any): void {
        this.currentPage = Math.floor(event.first / event.rows);
        this.rows = event.rows;
        this.loadCarClasses();
    }

    /**
     * Handles the search input change event.
     */
    public onSearchInputChange(): void {
        this.searchSubject.next(this.search);
    }

    /**
     * Opens the edit dialog.
     * @param carClass The car class to edit.
     */
    public editCarClass(carClass: CarClass): void {
        this.selectedCarClass = new CarClassWriteDTO(carClass);
        this.displayUpdateCarClassDialog = true;
    }

    /**
     * Deletes the car class.
     * @param carClass The car class to delete.
     */
    public deleteCarClass(carClass: CarClass): void {
        this.confirmationService.confirm({
            message: 'Are you sure you want to delete the car class ' + carClass.name + '?',
            header: 'Confirm Deletion',
            icon: 'pi pi-exclamation-triangle',
            accept: () => {
                this.carClassService.deleteCarClass(carClass.id).then(() => {
                    this.loadCarClasses();
                    this.messageService.add(
                        { severity: 'success', summary: 'Success', detail: 'Car class deleted', life: 3000 });
                }).catch((error) => {
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: error.message, life: 3000 });
                });
            }
        });
    }

    /**
     * Opens the create car class dialog.
     */
    public openCreateCarClassDialog(): void {
        this.displayCreateCarClassDialog = true;
    }

    /**
     * Reloads the car classes.
     */
    public reloadCarClasses(): void {
        this.loadCarClasses();
    }

    /**
     * Exports the car classes.
     */
    public exportCarClasses() {
        this.exportService.exportCarClasses().then(() => {
            this.messageService.add({
                severity: 'success',
                summary: 'Success',
                detail: 'Car classes exported successfully',
                life: 3000,
            });
        }).catch((error) => {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: error.message, life: 3000 });
        });
    }
}

